import React, { useState } from 'react';
import { Navigate } from 'react-router';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings, Save, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useGymSettings } from '@/hooks/useGymSettings';
import FileUpload from '@/components/gym/FileUpload';
import { useToast } from '@/hooks/use-toast';
import { clearSignedUrlCache, extractFileKeyFromUrl } from '@/utils/signedUrlCache';
import { organizationCache } from '@/utils/organizationCache';

const GymSettings = () => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const { isAdmin } = useRolePermissions();
  const { toast } = useToast();
  
  const {
    settings,
    isLoading,
    error,
    updateSettings,
    uploadFile,
    removeFile,
  } = useGymSettings();

  const [organizationName, setOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLightLogo, setIsUploadingLightLogo] = useState(false);
  const [isUploadingDarkLogo, setIsUploadingDarkLogo] = useState(false);
  const [isUploadingTerms, setIsUploadingTerms] = useState(false);
  const [isRemovingLightLogo, setIsRemovingLightLogo] = useState(false);
  const [isRemovingDarkLogo, setIsRemovingDarkLogo] = useState(false);
  const [isRemovingTerms, setIsRemovingTerms] = useState(false);

  // Update form values when settings are loaded
  React.useEffect(() => {
    if (settings) {
      setOrganizationName(settings.organization_name || '');
      setOrganizationSlug(settings.organization_slug || '');
    }
  }, [settings]);

  // Handle authentication loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>{t('common.loading')}...</span>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user is admin
  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="py-6">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-lg font-medium text-destructive mb-2">
                {t('gymSettings.accessDenied')}
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const handleSaveSettings = async () => {
    if (!organizationName.trim()) {
      toast({
        title: "Error",
        description: "Organization name is required",
        variant: "destructive",
      });
      return;
    }

    if (!organizationSlug.trim()) {
      toast({
        title: "Error", 
        description: "Organization slug is required",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const success = await updateSettings({ 
        organization_name: organizationName.trim(),
        organization_slug: organizationSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')
      });
      
      if (success) {
        // Clear organization cache when settings change
        organizationCache.clearCurrentOrganization();
        
        // Refresh the page to reload cached organization data
        window.location.reload();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleLightLogoUpload = async (file: File) => {
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingLightLogo(true);
    try {
      // Clear cache for old logo if it exists
      if (settings?.logo_light_url) {
        const oldFileKey = extractFileKeyFromUrl(settings.logo_light_url);
        if (oldFileKey) {
          clearSignedUrlCache(oldFileKey, 'gym-logos');
        }
      }

      const logoUrl = await uploadFile(file, 'gym-logos');
      if (logoUrl) {
        const success = await updateSettings({ logo_light_url: logoUrl });
        if (success) {
          // Clear organization cache when logo changes
          organizationCache.clearCurrentOrganization();
          
          // Refresh the page to reload cached organization data
          window.location.reload();
        }
      }
    } finally {
      setIsUploadingLightLogo(false);
    }
  };

  const handleDarkLogoUpload = async (file: File) => {
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingDarkLogo(true);
    try {
      // Clear cache for old logo if it exists
      if (settings?.logo_dark_url) {
        const oldFileKey = extractFileKeyFromUrl(settings.logo_dark_url);
        if (oldFileKey) {
          clearSignedUrlCache(oldFileKey, 'gym-logos');
        }
      }

      const logoUrl = await uploadFile(file, 'gym-logos');
      if (logoUrl) {
        const success = await updateSettings({ logo_dark_url: logoUrl });
        if (success) {
          // Clear organization cache when logo changes
          organizationCache.clearCurrentOrganization();
          
          // Refresh the page to reload cached organization data
          window.location.reload();
        }
      }
    } finally {
      setIsUploadingDarkLogo(false);
    }
  };

  const handleLightLogoRemove = async () => {
    if (!settings?.logo_light_url) return;

    console.log('🖼️ Starting light logo removal for:', settings.logo_light_url);
    setIsRemovingLightLogo(true);
    try {
      // Clear signed URL cache for the logo being removed
      const fileKey = extractFileKeyFromUrl(settings.logo_light_url);
      if (fileKey) {
        clearSignedUrlCache(fileKey, 'gym-logos');
      }

      const success = await removeFile(settings.logo_light_url, 'gym-logos');
      console.log('✅ Light logo removal from storage result:', success);
      
      if (success) {
        console.log('💾 Updating database to remove logo_light_url...');
        const updateResult = await updateSettings({ clear_light_logo: true });
        console.log('💾 Database update result:', updateResult);
        
        if (updateResult) {
          // Clear organization cache when logo is removed
          organizationCache.clearCurrentOrganization();
        }
        
        toast({
          title: "Success",
          description: "Light mode logo removed successfully",
        });
        
        // Refresh the page to reload cached organization data
        setTimeout(() => window.location.reload(), 1000);
      }
    } finally {
      setIsRemovingLightLogo(false);
    }
  };

  const handleDarkLogoRemove = async () => {
    if (!settings?.logo_dark_url) return;

    console.log('🖼️ Starting dark logo removal for:', settings.logo_dark_url);
    setIsRemovingDarkLogo(true);
    try {
      // Clear signed URL cache for the logo being removed
      const fileKey = extractFileKeyFromUrl(settings.logo_dark_url);
      if (fileKey) {
        clearSignedUrlCache(fileKey, 'gym-logos');
      }

      const success = await removeFile(settings.logo_dark_url, 'gym-logos');
      console.log('✅ Dark logo removal from storage result:', success);
      
      if (success) {
        console.log('💾 Updating database to remove logo_dark_url...');
        const updateResult = await updateSettings({ clear_dark_logo: true });
        console.log('💾 Database update result:', updateResult);
        
        if (updateResult) {
          // Clear organization cache when logo is removed
          organizationCache.clearCurrentOrganization();
        }
        
        toast({
          title: "Success",
          description: "Dark mode logo removed successfully",
        });
        
        // Refresh the page to reload cached organization data
        setTimeout(() => window.location.reload(), 1000);
      }
    } finally {
      setIsRemovingDarkLogo(false);
    }
  };

  const handleTermsUpload = async (file: File) => {
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Error",
        description: "Please select a PDF, DOC, or DOCX file",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingTerms(true);
    try {
      // Clear cache for old terms file if it exists
      if (settings?.terms_file_url) {
        const oldFileKey = extractFileKeyFromUrl(settings.terms_file_url);
        if (oldFileKey) {
          clearSignedUrlCache(oldFileKey, 'gym-documents');
        }
      }

      const termsUrl = await uploadFile(file, 'gym-documents');
      if (termsUrl) {
        const success = await updateSettings({ terms_file_url: termsUrl });
        if (success) {
          // Clear organization cache when terms file changes
          organizationCache.clearCurrentOrganization();
          
          // Refresh the page to reload cached organization data
          window.location.reload();
        }
      }
    } finally {
      setIsUploadingTerms(false);
    }
  };

  const handleTermsRemove = async () => {
    if (!settings?.terms_file_url) return;

    console.log('📄 Starting terms removal for:', settings.terms_file_url);
    setIsRemovingTerms(true);
    try {
      // Clear signed URL cache for the terms file being removed
      const fileKey = extractFileKeyFromUrl(settings.terms_file_url);
      if (fileKey) {
        clearSignedUrlCache(fileKey, 'gym-documents');
      }

      const success = await removeFile(settings.terms_file_url, 'gym-documents');
      console.log('✅ Terms removal from storage result:', success);
      
      if (success) {
        console.log('💾 Updating database to remove terms_file_url...');
        const updateResult = await updateSettings({ clear_terms: true });
        console.log('💾 Database update result:', updateResult);
        
        if (updateResult) {
          // Clear organization cache when terms file is removed
          organizationCache.clearCurrentOrganization();
        }
        
        toast({
          title: "Success",
          description: t('gymSettings.termsRemoved'),
        });
        
        // Refresh the page to reload cached organization data
        setTimeout(() => window.location.reload(), 1000);
      }
    } finally {
      setIsRemovingTerms(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="py-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="py-6">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-destructive">{error}</div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="py-4 md:py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-primary" />
          <h1 className="text-2xl md:text-3xl font-bold greek-text">
            {t('gymSettings.title')}
          </h1>
        </div>

        <div className="grid gap-6">
          {/* Organization Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="greek-text">{t('gymSettings.organizationDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="organizationName" className="greek-text">
                    {t('gymSettings.organizationName')}
                  </Label>
                  <Input
                    id="organizationName"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder={t('gymSettings.organizationNamePlaceholder')}
                    className="greek-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organizationSlug" className="greek-text">
                    {t('gymSettings.organizationSlug')}
                  </Label>
                  <Input
                    id="organizationSlug"
                    value={organizationSlug}
                    onChange={(e) => setOrganizationSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    placeholder={t('gymSettings.organizationSlugPlaceholder')}
                    className="greek-text"
                  />
                </div>
              </div>
              <Button
                onClick={handleSaveSettings}
                disabled={isSaving || !organizationName.trim() || !organizationSlug.trim()}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span className="greek-text">
                  {isSaving ? t('gymSettings.saving') : t('gymSettings.save')}
                </span>
              </Button>
            </CardContent>
          </Card>

          {/* Light Mode Logo Upload Section */}
          <FileUpload
            title={t('gymSettings.lightModeLogo')}
            description={t('gymSettings.lightModeLogoDescription')}
            currentFileKey={settings?.logo_light_url ?? null}
            onFileUpload={handleLightLogoUpload}
            onFileRemove={handleLightLogoRemove}
            acceptedFormats={t('gymSettings.acceptedFormats')}
            maxSize="max 5MB"
            isUploading={isUploadingLightLogo}
            isRemoving={isRemovingLightLogo}
            fileType="image"
          />

          {/* Dark Mode Logo Upload Section */}
          <FileUpload
            title={t('gymSettings.darkModeLogo')}
            description={t('gymSettings.darkModeLogoDescription')}
            currentFileKey={settings?.logo_dark_url ?? null}
            onFileUpload={handleDarkLogoUpload}
            onFileRemove={handleDarkLogoRemove}
            acceptedFormats={t('gymSettings.acceptedFormats')}
            maxSize="max 5MB"
            isUploading={isUploadingDarkLogo}
            isRemoving={isRemovingDarkLogo}
            fileType="image"
          />

          {/* Terms Upload Section */}
          <FileUpload
            title={t('gymSettings.terms')}
            description={t('gymSettings.termsDescription')}
            currentFileKey={settings?.terms_file_url ?? null}
            onFileUpload={handleTermsUpload}
            onFileRemove={handleTermsRemove}
            acceptedFormats={t('gymSettings.acceptedDocFormats')}
            maxSize="max 10MB"
            isUploading={isUploadingTerms}
            isRemoving={isRemovingTerms}
            fileType="document"
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default GymSettings;